import { Box, Typography } from "@mui/material"
import { If } from "lib/@components/switch"
import { Repeat } from "lib/@components/repeat"
import { PERIOD_MULTIPLIER, PERIOD_SORT, VALID_CLASSIFICATIONS } from "library/SFG20/constants"
import { PdfTimingTaskList } from "./pdf-timing-task-list"
import { PdfTimingSummaryRow } from "./pdf-timing-summary-row"
import { normaliseText } from "./pdf-formatters"

export function PdfTimingSummary({ originalTasks, draftTasks, draftTaskIndexLookup }) {
    const originalAnnual = calculateAnnualTiming(originalTasks)
    const draftAnnual = calculateAnnualTiming(draftTasks)
    const annualDelta = draftAnnual - originalAnnual
    const changedTasks = (draftTasks ?? []).filter((task) => hasTimingChanged(task, originalTasks))
    const groups = buildFrequencyGroups(changedTasks, draftTaskIndexLookup)
    const originalTaskIndexLookup = buildTaskIndexLookup(originalTasks)
    const removedTasks = (originalTasks ?? []).filter((task) => isRemovedTask(task, draftTasks))

    if (groups.length === 0 && removedTasks.length === 0 && annualDelta === 0) return null

    return (
        <Box sx={{ mb: "18px", pageBreakInside: "avoid", breakInside: "avoid" }}>
            <Box
                component="table"
                sx={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "10px",
                    border: "1px solid #d7dce2",
                    borderRadius: "6px",
                    overflow: "hidden",
                    "& th, & td": {
                        border: "1px solid #d7dce2",
                        p: "10px 12px",
                        textAlign: "left",
                        verticalAlign: "top",
                    },
                    "& th": { bgcolor: "#ebecef", fontWeight: 700, color: "#374151" },
                }}
            >
                <thead>
                    <tr>
                        <th>Timing Changes</th>
                        <th>Affected Tasks</th>
                    </tr>
                </thead>
                <tbody>
                    <Repeat list={groups} item={<PdfTimingSummaryRow />} />
                    <If value={removedTasks.length > 0}>
                        <tr>
                            <td>
                                <Typography sx={{ fontWeight: 700, color: "#b91c1c", fontSize: "10px" }}>
                                    Removed
                                </Typography>
                                <Typography sx={{ color: "#b91c1c", fontSize: "10px" }}>
                                    -{Math.round(calculateAnnualTiming(removedTasks))} mins
                                </Typography>
                            </td>
                            <td>
                                <PdfTimingTaskList
                                    items={removedTasks.map((task) => buildTaskListItem(task, originalTaskIndexLookup))}
                                    color="#b91c1c"
                                />
                            </td>
                        </tr>
                    </If>
                    <tr>
                        <td>
                            <Typography sx={{ fontWeight: 700, fontSize: "10px" }}>Annual Timing</Typography>
                        </td>
                        <td>
                            <Typography sx={{ fontWeight: 400, fontSize: "10px", color: "#374151" }}>
                                <Box component="span" sx={{ fontWeight: 700, color: "#1f2937" }}>
                                    {Math.round(draftAnnual)} mins
                                </Box>{" "}
                                <Box
                                    component="span"
                                    sx={{
                                        color: annualDelta > 0 ? "#2b95a2" : annualDelta < 0 ? "#b91c1c" : "#6b7280",
                                    }}
                                >
                                    ({annualDelta > 0 ? "+" : ""}
                                    {Math.round(annualDelta)} mins vs previous)
                                </Box>
                            </Typography>
                        </td>
                    </tr>
                </tbody>
            </Box>
        </Box>
    )
}

function calculateAnnualTiming(tasks = []) {
    return (tasks ?? []).filter(isTimingEligibleTask).reduce((total, task) => {
        const multiplier = PERIOD_MULTIPLIER[task?.frequency?.period] ?? 0
        const interval = task?.frequency?.interval || 1
        const minutes = task?.minutes ?? 0
        return total + (multiplier / interval) * minutes
    }, 0)
}

function buildFrequencyGroups(tasks = [], taskIndexLookup = {}) {
    const groups = {}

    for (const task of tasks) {
        if (!isTimingEligibleTask(task)) continue

        const key = `${task.frequency.interval || 1}${task.frequency.period}`
        if (!groups[key]) {
            groups[key] = {
                frequency: task.frequency,
                minutes: 0,
                taskEntries: [],
            }
        }

        groups[key].minutes += task.minutes ?? 0
        groups[key].taskEntries.push(buildTaskListItem(task, taskIndexLookup))
    }

    return Object.values(groups)
        .map((group) => ({ ...group, taskEntries: uniqueTaskEntries(group.taskEntries) }))
        .sort(compareFrequencyGroups)
}

function compareFrequencyGroups(a, b) {
    const aValue = (PERIOD_SORT[a.frequency?.period] ?? 999) * (a.frequency?.interval || 1)
    const bValue = (PERIOD_SORT[b.frequency?.period] ?? 999) * (b.frequency?.interval || 1)
    return aValue - bValue
}

function hasTimingChanged(draftTask, originalTasks = []) {
    if (!isTimingEligibleTask(draftTask)) return false

    const originalTask = (originalTasks ?? []).find(
        (task) => (task.taskId ?? task.id) === (draftTask.taskId ?? draftTask.id)
    )
    if (!originalTask) return true

    return (
        draftTask.minutes !== originalTask.minutes ||
        draftTask.frequency?.period !== originalTask.frequency?.period ||
        draftTask.frequency?.interval !== originalTask.frequency?.interval
    )
}

function isRemovedTask(originalTask, draftTasks = []) {
    if (!isTimingEligibleTask(originalTask)) return false

    return !(draftTasks ?? []).some((task) => (task.taskId ?? task.id) === (originalTask.taskId ?? originalTask.id))
}

function isTimingEligibleTask(task) {
    return !task?.isHeader && !!task?.frequency?.period && VALID_CLASSIFICATIONS.includes(task?.classification)
}

function buildTaskIndexLookup(tasks = []) {
    const lookup = {}
    let index = 0
    for (const task of tasks ?? []) {
        if (task?.isHeader) continue
        index += 1
        lookup[task.taskId ?? task.id] = index
    }
    return lookup
}

function buildTaskListItem(task, taskIndexLookup = {}) {
    const number = taskIndexLookup[task?.taskId ?? task?.id] ?? task?.displayIndex ?? task?.index ?? null
    const title = normaliseText(task?.title) || "Untitled task"

    return {
        key: `${task?.taskId ?? task?.id ?? title}:${number ?? "none"}`,
        label: number != null ? `Task ${number} - ${title}` : title,
    }
}

function uniqueTaskEntries(taskEntries = []) {
    const seen = new Set()
    const output = []

    for (const item of taskEntries) {
        if (!item?.key || seen.has(item.key)) continue
        seen.add(item.key)
        output.push(item)
    }

    return output
}
